/**
 * ============================================================================
 * LIL' LEO — MASTER AJAX CART & DRAWER ENGINE (Vanilla JS / Shopify OS 2.0)
 * ============================================================================
 * Direkte, robuste Anbindung an Shopifys Standard AJAX Cart API ohne
 * fehleranfällige Dawn Web-Component Abhängigkeiten.
 */

(function () {
  'use strict';

  // Konfiguration
  function getShippingThresholdCents() {
    const el = document.getElementById('shipping-progress-container');
    if (el && el.dataset.thresholdCents) {
      const parsed = parseInt(el.dataset.thresholdCents, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    const saleEl = document.getElementById('sale-progress-container');
    if (saleEl && saleEl.dataset.shippingThreshold) {
      const parsedSale = parseInt(saleEl.dataset.shippingThreshold, 10);
      if (!isNaN(parsedSale) && parsedSale > 0) return parsedSale;
    }
    return 8500;
  }

  // Gratis-Versand-Fortschritt auf Basis des rabattierten Warenwerts (cart.total_price)
  function getShippingProgress(cart, threshold) {
    const net = cart.total_price || 0;
    const reached = net >= threshold && cart.item_count > 0;
    const remaining = Math.max(0, threshold - net);
    const percent = threshold > 0 ? Math.min(100, Math.round((net / threshold) * 100)) : 100;
    return { reached, remaining, percent };
  }

  // Hilfsfunktion: Preis in Euro formatieren
  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }

  // Toast Benachrichtigung
  window.showToast = function (message) {
    let toast = document.getElementById('toast-container');
    if (!toast) {
      const div = document.createElement('div');
      div.innerHTML = `
        <div id="toast-container" class="fixed bottom-6 right-6 z-[99999] transform translate-y-24 opacity-0 transition-all duration-300 pointer-events-none">
          <div class="bg-[#1E2B3D] text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border border-white/20 text-xs font-semibold font-franklin">
            <span class="text-[#E4B0B3] text-base font-bold">✓</span>
            <span id="toast-text">${message || 'Aktion ausgeführt!'}</span>
          </div>
        </div>
      `;
      document.body.appendChild(div.firstElementChild);
      toast = document.getElementById('toast-container');
    }
    const text = document.getElementById('toast-text');
    if (text) text.innerText = message;
    toast.classList.remove('translate-y-24', 'opacity-0', 'pointer-events-none');
    toast.classList.add('translate-y-0', 'opacity-100');
    setTimeout(() => {
      toast.classList.remove('translate-y-0', 'opacity-100');
      toast.classList.add('translate-y-24', 'opacity-0', 'pointer-events-none');
    }, 2800);
  };

  // --------------------------------------------------------------------------
  // GLOBAL AJAX CART INTERCEPTOR (Fetch & XMLHttpRequest)
  // Ensures any 3rd-party widget (e.g., Fastbundle, app embeds, scripts)
  // that adds or modifies items in the cart automatically updates and opens
  // the native Lil' Leo Cart Drawer.
  // --------------------------------------------------------------------------
  (function initGlobalCartInterceptor() {
    if (window.__LilLeoCartInterceptorInstalled) return;
    window.__LilLeoCartInterceptorInstalled = true;

    let debounceTimer = null;
    function scheduleCartSync(shouldOpen, toastTitle) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (window.LilLeoCart && typeof window.LilLeoCart.fetchCart === 'function') {
          window.LilLeoCart.fetchCart(() => {
            if (shouldOpen && typeof window.LilLeoCart.open === 'function') {
              window.LilLeoCart.open();
            }
            if (toastTitle && typeof window.showToast === 'function') {
              window.showToast(`✓ "${toastTitle}" im Warenkorb!`);
            }
          });
        }
      }, 120);
    }

    // 1. Intercept window.fetch
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      const isCartAdd = typeof url === 'string' && url.includes('/cart/add');
      const isCartChange = typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'));

      return originalFetch.apply(this, args).then(response => {
        if (response && response.ok && !window.__LilLeoInternalFetch) {
          if (isCartAdd) {
            try {
              response.clone().json().then(data => {
                const title = data.title || (data.items && data.items[0] && data.items[0].title) || '';
                scheduleCartSync(true, title);
              }).catch(() => {
                scheduleCartSync(true);
              });
            } catch (e) {
              scheduleCartSync(true);
            }
          } else if (isCartChange) {
            scheduleCartSync(false);
          }
        }
        return response;
      });
    };

    // 2. Intercept XMLHttpRequest
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__lilleo_url = typeof url === 'string' ? url : '';
      return originalXhrOpen.apply(this, [method, url, ...rest]);
    };
    XMLHttpRequest.prototype.send = function (...args) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300 && !window.__LilLeoInternalFetch) {
          const url = this.__lilleo_url || '';
          if (url.includes('/cart/add')) {
            scheduleCartSync(true);
          } else if (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear')) {
            scheduleCartSync(false);
          }
        }
      });
      return originalXhrSend.apply(this, args);
    };
  })();

  // Lil' Leo Cart Controller
  const LilLeoCart = {
    isOpen: false,
    cartData: null,

    init: function () {
      this.bindEvents();
      this.fetchCart();
    },

    bindEvents: function () {
      // 1. Header & Trigger Buttons
      document.addEventListener('click', (e) => {
        const openTrigger = e.target.closest('#header-cart-btn, #cart-icon-bubble, [data-open-cart]');
        if (openTrigger) {
          e.preventDefault();
          this.open();
          return;
        }

        const closeTrigger = e.target.closest('#cart-backdrop, .drawer__close, [data-close-cart]');
        if (closeTrigger) {
          e.preventDefault();
          this.close();
          return;
        }

        // Quick Add Buttons
        const quickAddBtn = e.target.closest('[data-quick-add]');
        if (quickAddBtn) {
          e.preventDefault();
          const variantId = quickAddBtn.dataset.quickAdd;
          const title = quickAddBtn.dataset.title || 'Artikel';
          if (variantId) {
            this.addItem(variantId, 1, title);
          }
        }
      });

      // 2. Escape Taste zum Schließen
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // 3. Intercept Product Forms (Ajax Add to Cart)
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (form) {
          e.preventDefault();
          const formData = new FormData(form);
          const submitBtn = form.querySelector('[type="submit"], [name="add"]');
          const originalText = submitBtn ? submitBtn.innerHTML : '';

          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<span class="inline-block animate-pulse">Wird hinzugefügt...</span>';
          }

          window.__LilLeoInternalFetch = true;
          fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
            .then(res => res.json())
            .then(item => {
              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalText;
              }
              if (item.status && item.status >= 400) {
                window.showToast(`Fehler: ${item.description || item.message}`);
              } else {
                this.fetchCart(() => {
                  this.open();
                  window.showToast(`✓ "${item.title || 'Artikel'}" im Warenkorb!`);
                });
              }
            })
            .catch(err => {
              console.error('Cart add error:', err);
              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalText;
              }
            })
            .finally(() => {
              window.__LilLeoInternalFetch = false;
            });
        }
      });
    },

    open: function () {
      const container = document.getElementById('cart-drawer-container');
      const backdrop = document.getElementById('cart-backdrop');
      const panel = document.getElementById('cart-panel');

      if (!container || !panel) return;

      this.isOpen = true;
      container.style.display = 'block';
      container.classList.remove('pointer-events-none');
      void container.offsetWidth; // Force layout reflow
      container.classList.add('is-open');
      document.body.classList.add('overflow-hidden');

      // Animierter Slide-In
      requestAnimationFrame(() => {
        if (backdrop) {
          backdrop.style.display = 'block';
          backdrop.classList.remove('opacity-0', 'pointer-events-none');
          backdrop.classList.add('opacity-100', 'pointer-events-auto');
        }
        panel.classList.remove('translate-x-full');
        panel.classList.add('translate-x-0');
      });
    },

    close: function () {
      const container = document.getElementById('cart-drawer-container');
      const backdrop = document.getElementById('cart-backdrop');
      const panel = document.getElementById('cart-panel');

      if (!container || !panel) return;

      this.isOpen = false;
      document.body.classList.remove('overflow-hidden');
      container.classList.remove('is-open');

      if (backdrop) {
        backdrop.classList.remove('opacity-100', 'pointer-events-auto');
        backdrop.classList.add('opacity-0', 'pointer-events-none');
      }
      panel.classList.remove('translate-x-0');
      panel.classList.add('translate-x-full');

      setTimeout(() => {
        if (!this.isOpen) {
          container.classList.add('pointer-events-none');
          container.style.display = 'none';
        }
      }, 300);
    },

    fetchCart: function (callback) {
      return fetch('/cart.js', {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(res => res.json())
        .then(cart => {
          this.cartData = cart;
          this.render(cart);
          if (typeof callback === 'function') callback(cart);
          return cart;
        })
        .catch(err => console.error('Cart fetch error:', err));
    },

    addItem: function (variantOrHandle, quantity = 1, title = 'Artikel') {
      const doAdd = (variantId, itemTitle) => {
        window.__LilLeoInternalFetch = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        })
          .then(res => res.json())
          .then(item => {
            if (item.status && item.status >= 400) {
              window.showToast(`Fehler: ${item.description || item.message}`);
            } else {
              this.fetchCart(() => {
                this.open();
                window.showToast(`✓ "${itemTitle || title}" hinzugefügt!`);
              });
            }
          })
          .catch(err => console.error('Error adding item to cart:', err))
          .finally(() => {
            window.__LilLeoInternalFetch = false;
          });
      };

      if (!isNaN(variantOrHandle) && parseInt(variantOrHandle, 10) > 0) {
        doAdd(parseInt(variantOrHandle, 10), title);
      } else {
        fetch(`/products/${variantOrHandle}.js`)
          .then(res => res.json())
          .then(product => {
            const variant = product.variants?.find(v => v.available) || product.variants?.[0];
            if (variant) {
              doAdd(variant.id, product.title);
            } else {
              window.showToast(`Artikel aktuell nicht verfügbar.`);
            }
          })
          .catch(err => {
            console.error('Product fetch error:', err);
            window.showToast(`Artikel konnte nicht geladen werden.`);
          });
      }
    },

    updateQuantity: function (lineKey, newQty) {
      window.__LilLeoInternalFetch = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: lineKey, quantity: newQty })
      })
        .then(res => res.json())
        .then(cart => {
          this.cartData = cart;
          this.render(cart);
        })
        .catch(err => console.error('Error updating quantity:', err))
        .finally(() => {
          window.__LilLeoInternalFetch = false;
        });
    },

    render: function (cart) {
      if (!cart) return;

      // 1. Synchronisiere Header Badges & Zähler
      const headerCounts = document.querySelectorAll('#header-cart-count, .cart-count-bubble');
      headerCounts.forEach(el => {
        el.innerText = cart.item_count;
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
        }
      });

      const headerTotals = document.querySelectorAll('#header-cart-total');
      headerTotals.forEach(el => {
        el.innerText = formatMoney(cart.total_price);
      });

      // 2. Drawer Zähler Badge
      const drawerCountBadge = document.getElementById('cart-drawer-count-badge');
      if (drawerCountBadge) {
        drawerCountBadge.innerText = `${cart.item_count} ${cart.item_count === 1 ? 'Artikel' : 'Artikel'} ausgewählt`;
      }

      // 3. Freiversand-Balken (85 € Schwelle)
      const progressContainer = document.getElementById('shipping-progress-container');
      const progressText = document.getElementById('shipping-progress-text');
      const progressPercentEl = document.getElementById('shipping-progress-percent');
      const progressBar = document.getElementById('shipping-progress-bar');

      if (progressContainer && progressText && progressPercentEl && progressBar) {
        const threshold = getShippingThresholdCents();
        const shipInfo = getShippingProgress(cart, threshold);
        const remainingCents = shipInfo.remaining;
        const percent = shipInfo.percent;

        progressPercentEl.innerText = `${percent}%`;
        progressBar.style.display = 'block';
        progressBar.style.width = `${percent}%`;

        const textSuccess = progressContainer.dataset.textSuccess || '✓ Glückwunsch! Kostenloser DHL-Versand aktiviert!';
        const textProgress = progressContainer.dataset.textProgress || 'Noch [amount] bis zum kostenlosen DHL-Versand!';

        if (shipInfo.reached) {
          progressText.innerHTML = `<span class="text-emerald-800 font-bold">${textSuccess}</span>`;
          progressBar.style.backgroundColor = '#059669';
        } else {
          let progressHtml = textProgress.replace('[amount]', `<strong class="text-[#92834C] font-bold">${formatMoney(remainingCents)}</strong>`);
          progressText.innerHTML = `<span>${progressHtml}</span>`;
          progressBar.style.backgroundColor = '#92834C';
        }
      }

      // 3b. Sale Staffelrabatt-Balken (Dynamisch für 1, 2 oder 3 Stufen)
      const saleContainer = document.getElementById('sale-progress-container');
      if (saleContainer) {
        const hasBase = saleContainer.dataset.hasBase === 'true';
        const baseDiscount = saleContainer.dataset.baseDiscount || '';
        const hasTier1 = saleContainer.dataset.hasTier1 === 'true';
        const tier1Threshold = parseInt(saleContainer.dataset.tier1Threshold, 10) || 0;
        const tier1Discount = saleContainer.dataset.tier1Discount || '';
        const hasTier2 = saleContainer.dataset.hasTier2 === 'true';
        const tier2Threshold = parseInt(saleContainer.dataset.tier2Threshold, 10) || 0;
        const tier2Discount = saleContainer.dataset.tier2Discount || '';
        const includeShipping = saleContainer.dataset.includeShipping === 'true';
        const shippingThreshold = parseInt(saleContainer.dataset.shippingThreshold, 10) || 0;

        // Rabattstufen (100 € / 150 €): Shopify schaltet LEO20 / LEO25 anhand des Warenwerts VOR Rabatt (ohne Geschenkgutscheine) frei.
        // Gratis-Versand (85 €): Shopify prüft die Versandschwelle anhand des Zahlbetrags NACH Rabatt (cart.total_price).
        const discountTotal = (cart.items && cart.items.length > 0)
          ? cart.items.reduce((sum, it) => it.gift_card ? sum : sum + (it.original_line_price || 0), 0)
          : (cart.original_total_price || cart.total_price || 0);
        const shipTotal = cart.total_price || 0;
        const valueFor = (m) => m.isShipping ? shipTotal : discountTotal;
        const isMilestoneReached = (m) => valueFor(m) >= m.threshold;

        // Alle aktiven Meilensteine sammeln
        const milestones = [];
        if (includeShipping && shippingThreshold > 0) {
          milestones.push({
            id: 'shipping',
            threshold: shippingThreshold,
            discount: 'Gratis-Versand',
            isShipping: true,
            dotEl: document.getElementById('sale-step-shipping-dot'),
            valEl: document.getElementById('sale-step-shipping-val'),
            subEl: document.getElementById('sale-step-shipping-sub')
          });
        }
        if (hasTier1 && tier1Threshold > 0) {
          milestones.push({
            id: 'tier1',
            threshold: tier1Threshold,
            discount: tier1Discount,
            isShipping: false,
            dotEl: document.getElementById('sale-step-1-dot'),
            valEl: document.getElementById('sale-step-1-label-val'),
            subEl: document.getElementById('sale-step-1-label-sub')
          });
        }
        if (hasTier2 && tier2Threshold > 0) {
          milestones.push({
            id: 'tier2',
            threshold: tier2Threshold,
            discount: tier2Discount,
            isShipping: false,
            dotEl: document.getElementById('sale-step-2-dot'),
            valEl: document.getElementById('sale-step-2-label-val'),
            subEl: document.getElementById('sale-step-2-label-sub')
          });
        }

        // Nach Schwellenwert aufsteigend sortieren (85 € Gratis-Versand -> 100 € 20% -> 150 € 25%)
        milestones.sort((a, b) => a.threshold - b.threshold);
        const mCount = milestones.length;

        // Dynamische Fortschrittsberechnung (Step-basiert je nach Meilenstein-Anzahl)
        let salePercent = 0;
        if (mCount === 0 || (discountTotal <= 0 && shipTotal <= 0)) {
          salePercent = 0;
        } else if (milestones.every(isMilestoneReached)) {
          salePercent = 100;
        } else if (mCount === 1) {
          salePercent = Math.min(100, Math.round((valueFor(milestones[0]) / milestones[0].threshold) * 100));
        } else if (mCount === 2) {
          if (!isMilestoneReached(milestones[0])) {
            salePercent = Math.round((valueFor(milestones[0]) / milestones[0].threshold) * 50);
          } else {
            const diff = Math.max(0, valueFor(milestones[1]) - milestones[0].threshold);
            const range = Math.max(1, milestones[1].threshold - milestones[0].threshold);
            salePercent = Math.min(99, Math.round(50 + (diff / range) * 50));
          }
        } else if (mCount === 3) {
          if (!isMilestoneReached(milestones[0])) {
            salePercent = Math.round((valueFor(milestones[0]) / milestones[0].threshold) * 33.3);
          } else if (!isMilestoneReached(milestones[1])) {
            const diff = Math.max(0, valueFor(milestones[1]) - milestones[0].threshold);
            const range = Math.max(1, milestones[1].threshold - milestones[0].threshold);
            salePercent = Math.min(66.6, Math.round(33.3 + (diff / range) * 33.3));
          } else {
            const diff = Math.max(0, valueFor(milestones[2]) - milestones[1].threshold);
            const range = Math.max(1, milestones[2].threshold - milestones[1].threshold);
            salePercent = Math.min(99, Math.round(66.6 + (diff / range) * 33.4));
          }
        }

        // Balkenbreite aktualisieren
        const saleBar = document.getElementById('sale-progress-bar');
        if (saleBar) {
          saleBar.style.display = 'block';
          saleBar.style.width = `${salePercent}%`;
        }

        // Dots & Labels dynamisch aktualisieren
        milestones.forEach(m => {
          if (m.dotEl) {
            const isReached = isMilestoneReached(m) && cart.item_count > 0;
            if (isReached) {
              m.dotEl.className = m.isShipping
                ? 'sale-milestone-dot is-shipping is-reached'
                : 'sale-milestone-dot is-reached';
              if (m.valEl) {
                m.valEl.className = m.isShipping
                  ? 'block font-bold text-emerald-800 text-xs'
                  : 'block font-bold text-[#D66152] text-xs';
                m.valEl.innerText = m.isShipping ? '✓ Gratis' : `✓ ${m.discount}`;
              }
              if (m.subEl) {
                m.subEl.className = m.isShipping ? 'text-[10px] text-emerald-700/80' : 'text-[10px] text-[#D66152]/80';
              }
            } else {
              m.dotEl.className = m.isShipping
                ? 'sale-milestone-dot is-shipping'
                : 'sale-milestone-dot';
              if (m.valEl) {
                m.valEl.className = 'block font-medium text-gray-700 text-xs';
                m.valEl.innerText = m.isShipping ? '📦 Gratis' : m.discount;
              }
              if (m.subEl) {
                m.subEl.className = 'text-[10px] text-gray-400';
              }
            }
          }
        });

        // 1. Textzeilen dynamisch je nach nächstem Ziel
        const statusTitle = document.getElementById('sale-current-status-title');
        const nextStepHint = document.getElementById('sale-next-step-hint');

        if (statusTitle && nextStepHint) {
          if (cart.item_count === 0) {
            if (hasBase) {
              statusTitle.innerHTML = `Du erhältst <span class="text-[#D66152] font-bold">${baseDiscount} Rabatt</span> auf alles.`;
            } else {
              statusTitle.innerText = 'Sichere dir jetzt attraktive Mengen-Rabatte!';
            }
            if (mCount > 0) {
              const firstM = milestones[0];
              if (firstM.isShipping) {
                nextStepHint.innerHTML = `Ab ${Math.round(firstM.threshold / 100)} € ist der Versand kostenlos!`;
              } else {
                nextStepHint.innerHTML = `Ab ${Math.round(firstM.threshold / 100)} € sparst du <strong class="text-[#D66152] font-bold">${firstM.discount}</strong>!`;
              }
            } else {
              nextStepHint.innerText = 'Der Rabatt wird im Checkout automatisch für dich abgezogen.';
            }
          } else if (mCount > 0 && milestones.every(isMilestoneReached)) {
            const lastM = milestones[mCount - 1];
            if (lastM.isShipping) {
              statusTitle.innerHTML = `✓ <span class="text-emerald-800 font-bold">Kostenloser DHL-Versand gesichert!</span>`;
            } else {
              statusTitle.innerHTML = `🎉 <span class="text-[#D66152] font-bold">Du erhältst den Höchstrabatt von ${lastM.discount}!</span>`;
            }
            nextStepHint.innerText = 'Du sparst das Maximum auf deine gesamte Bestellung.';
          } else {
            // Aktuell höchsten freigeschalteten Rabatt finden
            let curDiscount = hasBase ? baseDiscount : null;
            let shippingUnlocked = false;
            let nextMilestone = null;

            milestones.forEach(m => {
              if (isMilestoneReached(m)) {
                if (m.isShipping) shippingUnlocked = true;
                else curDiscount = m.discount;
              } else if (!nextMilestone) {
                nextMilestone = m;
              }
            });

            if (curDiscount && shippingUnlocked) {
              statusTitle.innerHTML = `Du erhältst aktuell <span class="text-[#D66152] font-bold">${curDiscount} Rabatt</span> &amp; <span class="text-emerald-800 font-bold">Gratis-Versand</span>.`;
            } else if (shippingUnlocked) {
              statusTitle.innerHTML = `✓ <span class="text-emerald-800 font-bold">Kostenloser DHL-Versand</span> gesichert!`;
            } else if (curDiscount) {
              statusTitle.innerHTML = `Du erhältst aktuell <span class="text-[#D66152] font-bold">${curDiscount} Rabatt</span>.`;
            } else {
              statusTitle.innerText = 'Lege weitere Artikel hinein, um Rabatte freizuschalten.';
            }

            if (nextMilestone) {
              const remaining = Math.max(0, nextMilestone.threshold - valueFor(nextMilestone));
              if (nextMilestone.isShipping) {
                nextStepHint.innerHTML = `Noch <strong class="text-[#D66152] font-bold">${formatMoney(remaining)}</strong> bis zum <strong class="text-gray-900 font-bold">kostenlosen DHL-Versand</strong>!`;
              } else {
                nextStepHint.innerHTML = `Noch <strong class="text-[#D66152] font-bold">${formatMoney(remaining)}</strong>, dann sparst du <strong class="text-[#D66152] font-bold">${nextMilestone.discount}</strong>!`;
              }
            } else {
              nextStepHint.innerText = 'Der Rabatt wird im Checkout automatisch abgezogen.';
            }
          }
        }
      }

      // 4. Empty State vs Items List
      const emptyState = document.getElementById('cart-empty-state');
      const itemsContainer = document.getElementById('cart-items-container');
      const footerArea = document.getElementById('cart-footer-area');

      if (cart.item_count === 0) {
        if (emptyState) emptyState.classList.remove('hidden');
        if (itemsContainer) itemsContainer.innerHTML = '';
        if (footerArea) footerArea.classList.add('hidden');
      } else {
        if (emptyState) emptyState.classList.add('hidden');
        if (footerArea) footerArea.classList.remove('hidden');

        // Render Cart Items
        if (itemsContainer) {
          itemsContainer.innerHTML = cart.items.map((item) => {
            const itemImage = item.featured_image ? (item.featured_image.url || item.featured_image) : (item.image || '');
            const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

            // Handle Bundle / Line Discounts
            // item.total_discount enthaelt auch den anteiligen Warenkorb-Rabatt (z. B. automatischer Staffelrabatt)
            const displayLinePrice = Math.min(
              item.final_line_price || 0,
              (item.original_line_price || 0) - (item.total_discount || 0)
            );
            const hasDiscount = item.original_line_price && (item.original_line_price > displayLinePrice);
            const priceHtml = hasDiscount
              ? `<span class="text-emerald-700 font-bold">${formatMoney(displayLinePrice)}</span><span class="line-through text-charcoal/60 font-normal">${formatMoney(item.original_line_price)}</span>`
              : `<span class="font-bold text-charcoal">${formatMoney(item.final_line_price)}</span>`;

            // Visible properties (exclude internal properties like _Bundle, __cartBundleId)
            const visibleProps = item.properties
              ? Object.entries(item.properties).filter(([k, v]) => !k.startsWith('_') && v)
              : [];
            const propsHtml = visibleProps.length > 0
              ? `<div class="mt-0.5 space-y-0.5">${visibleProps.map(([k, v]) => `<div class="text-[11px] text-charcoal/70 font-franklin">${k}: ${v}</div>`).join('')}</div>`
              : '';

            return `
              <div class="p-3.5 rounded-2xl bg-[#F9F6EB] flex gap-3.5 items-center justify-between border border-[#E8E2D5]/60 shadow-2xs" data-line-key="${item.key}">
                <div class="flex items-center gap-3.5 min-w-0">
                  <a href="${item.url}" class="shrink-0 group block leading-none">
                    ${itemImage ? `<img src="${itemImage}" alt="${item.title}" class="w-16 h-16 rounded-xl object-cover bg-white border border-[#E8E2D5]/50 group-hover:scale-105 transition-transform duration-200 block" width="64" height="64">` : `<div class="w-16 h-16 rounded-xl bg-white border border-[#E8E2D5]/50"></div>`}
                  </a>
                  <div class="min-w-0">
                    <a href="${item.url}" class="hover:text-olive transition-colors block no-underline">
                      <h4 class="font-alegreya font-medium text-base text-charcoal truncate leading-tight hover:text-olive transition-colors m-0 tracking-normal">${item.product_title || item.title}</h4>
                    </a>
                    ${variantTitle ? `<div class="cart-item-variant text-[13px] text-charcoal/80 truncate font-franklin mt-0.5">${variantTitle}</div>` : ''}
                    ${propsHtml}
                    <div class="cart-item-price text-base sm:text-[17px] mt-1 font-alegreya flex items-baseline gap-2">${priceHtml}</div>
                  </div>
                </div>
                <div class="flex flex-col items-center justify-center gap-1.5 shrink-0">
                  <div class="flex items-center rounded-full bg-white border border-[#E8E2D5] px-2.5 py-1 text-sm shadow-2xs">
                    <button type="button" onclick="window.LilLeoCart.updateQuantity('${item.key}', ${item.quantity - 1})" class="w-6 h-6 flex items-center justify-center text-charcoal hover:text-olive font-bold text-sm cursor-pointer bg-transparent border-0 p-0" aria-label="Menge verringern">-</button>
                    <span class="w-6 text-center font-bold text-sm font-franklin">${item.quantity}</span>
                    <button type="button" onclick="window.LilLeoCart.updateQuantity('${item.key}', ${item.quantity + 1})" class="w-6 h-6 flex items-center justify-center text-charcoal hover:text-olive font-bold text-sm cursor-pointer bg-transparent border-0 p-0" aria-label="Menge erhöhen">+</button>
                  </div>
                  <button type="button" onclick="window.LilLeoCart.updateQuantity('${item.key}', 0)" class="cart-item-remove w-full text-center text-xs text-gray-500 hover:text-rose transition-colors cursor-pointer bg-transparent border-0 p-0 hover:underline mt-0.5" aria-label="Entfernen">
                    <span>Entfernen</span>
                  </button>
                </div>
              </div>
            `;
          }).join('');
        }

        // 4. Totals, Discount & Shipping
        const discountRow = document.getElementById('cart-discount-row');
        const discountLabel = document.getElementById('cart-discount-label');
        const discountAmountEl = document.getElementById('cart-discount-amount');
        const discountInput = document.getElementById('cart-discount-input');

        const appliedDiscount = cart.discount_codes && cart.discount_codes.find(d => d.applicable);
        const totalDiscount = cart.total_discount || 0;

        if (totalDiscount > 0 || appliedDiscount) {
          if (discountRow) {
            discountRow.classList.remove('hidden');
            discountRow.classList.add('flex');
          }
          const codeName = (appliedDiscount && appliedDiscount.code)
            || (cart.cart_level_discount_applications && cart.cart_level_discount_applications[0]?.title)
            || sessionStorage.getItem('lilleo_discount_code')
            || '';
          if (discountLabel) {
            if (codeName && !appliedDiscount) {
              // Automatischer Rabatt (z. B. Staffelrabatt) kann nicht entfernt werden
              discountLabel.innerHTML = `Rabatt (${codeName}):`;
            } else if (codeName) {
              discountLabel.innerHTML = `Rabatt (${codeName}): <button type="button" onclick="window.removeCartDiscount()" class="text-xs text-[#D66152] hover:underline ml-1 underline cursor-pointer bg-transparent border-0 p-0 font-normal font-franklin">Entfernen</button>`;
            } else {
              discountLabel.innerHTML = `Gutschein-Rabatt: <button type="button" onclick="window.removeCartDiscount()" class="text-xs text-[#D66152] hover:underline ml-1 underline cursor-pointer bg-transparent border-0 p-0 font-normal font-franklin">Entfernen</button>`;
            }
          }
          if (discountAmountEl) {
            discountAmountEl.innerText = `-${formatMoney(totalDiscount)}`;
          }
          if (discountInput && appliedDiscount && appliedDiscount.code && !discountInput.value) {
            discountInput.value = codeName;
          }
        } else {
          if (discountRow) {
            discountRow.classList.add('hidden');
            discountRow.classList.remove('flex');
          }
          if (cart.discount_codes && cart.discount_codes.length === 0) {
            sessionStorage.removeItem('lilleo_discount_code');
          }
        }

        const subtotal = (totalDiscount > 0 && cart.original_total_price)
          ? cart.original_total_price
          : (cart.items_subtotal_price || cart.total_price);
        const subtotalEl = document.getElementById('cart-subtotal-price');
        if (subtotalEl) subtotalEl.innerText = formatMoney(subtotal);

        const cartPanel = document.getElementById('cart-panel');
        const countryCode = (cartPanel && cartPanel.dataset && cartPanel.dataset.countryCode) ? cartPanel.dataset.countryCode : 'DE';
        const isGermany = countryCode === 'DE';

        const isFreeShipping = isGermany && (cart.total_price >= getShippingThresholdCents() || cart.item_count === 0);
        const shippingCostCents = isGermany ? (isFreeShipping ? 0 : 490) : 0;
        const shippingEl = document.getElementById('cart-shipping-price');
        if (shippingEl) {
          if (isGermany) {
            if (isFreeShipping) {
              shippingEl.innerHTML = `<span class="text-emerald-700 font-bold">Kostenlos</span>`;
            } else {
              shippingEl.innerText = '4,90 €';
            }
          } else {
            shippingEl.innerHTML = `<span class="text-xs sm:text-sm font-normal text-charcoalMuted">Wird beim Checkout berechnet</span>`;
          }
        }

        const finalTotal = cart.total_price + shippingCostCents;
        const totalEl = document.getElementById('cart-total-price');
        if (totalEl) totalEl.innerText = formatMoney(finalTotal);
      }

      // 5. Dynamic Upsells Filter & Rendering (Exclude items already in cart, max 3)
      const upsellWrapper = document.getElementById('cart-upsell-wrapper');
      const upsellList = document.getElementById('cart-upsell-list');
      const candidatesScript = document.getElementById('cart-upsell-candidates');

      if (upsellWrapper && upsellList && candidatesScript) {
        try {
          const candidates = JSON.parse(candidatesScript.textContent || '[]');
          const cartProductIds = new Set(cart.items.map(item => Number(item.product_id)));
          const cartHandles = new Set(cart.items.map(item => String(item.handle).toLowerCase()));

          // Filter out candidates that are currently in the cart or not available
          const availableUpsells = candidates
            .filter(prod => {
              if (prod.available === false) return false;
              if (cartProductIds.has(Number(prod.id))) return false;
              if (prod.handle && cartHandles.has(String(prod.handle).toLowerCase())) return false;
              return true;
            })
            .slice(0, 3);

          if (availableUpsells.length === 0) {
            upsellWrapper.classList.add('hidden');
          } else {
            upsellWrapper.classList.remove('hidden');
            upsellList.innerHTML = availableUpsells.map(prod => {
              const formattedPrice = (prod.price || '').replace(/(\d)€/g, '$1 €');
              return `
              <div class="cart-upsell-card p-3 sm:p-3.5 rounded-2xl bg-white border border-solid border-[#E8E2D5] flex items-center justify-between gap-2.5 sm:gap-3.5 shadow-2xs" data-upsell-id="${prod.id}">
                <div class="flex items-center gap-2.5 sm:gap-3.5 min-w-0">
                  <a href="${prod.url || '#'}" class="shrink-0 group">
                    <img src="${prod.image || ''}" alt="${prod.title}" class="w-16 h-16 rounded-xl object-cover bg-[#F9F6EB] border border-[#E8E2D5]/50 group-hover:scale-105 transition-transform duration-200" width="64" height="64">
                  </a>
                  <div class="min-w-0">
                    <a href="${prod.url || '#'}" class="hover:text-olive transition-colors block no-underline">
                      <h5 class="cart-upsell-title font-alegreya font-medium text-base text-charcoal line-clamp-2 leading-snug hover:text-olive transition-colors m-0">${prod.title}</h5>
                    </a>
                    ${/geschenkbox|gutschein/i.test(prod.title || '') ? '' : '<div class="cart-upsell-subtitle text-[13px] text-charcoal/80 truncate font-franklin mt-0.5">100% reine Merinowolle</div>'}
                    <div class="cart-upsell-price text-base sm:text-[17px] font-bold text-charcoal font-alegreya mt-0.5">${formattedPrice}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onclick="window.LilLeoCart.addItem(${prod.variant_id || `'${prod.handle}'`}, 1, '${(prod.title || '').replace(/'/g, "\\'")}')"
                  class="btn-clean shrink-0 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer bg-[#92834C] hover:brightness-105 text-[#F9F6EB] shadow-2xs border-0 flex items-center gap-1"
                >
                  <span>+ Hinzufügen</span>
                </button>
              </div>
            `;
            }).join('');
          }
        } catch (e) {
          console.error('Error rendering upsells:', e);
        }
      }
    }
  };

  // Global zur Verfügung stellen
  window.LilLeoCart = LilLeoCart;
  window.openCartDrawer = () => LilLeoCart.open();
  window.closeCartDrawer = () => LilLeoCart.close();
  window.toggleCartDrawer = (open) => open ? LilLeoCart.open() : LilLeoCart.close();

  // Gutscheincode anwenden mit echter Shopify-Validierung
  window.applyCartDiscount = async function () {
    const input = document.getElementById('cart-discount-input');
    const msg = document.getElementById('cart-discount-message');
    const btn = document.getElementById('cart-discount-apply-btn') || document.querySelector('button[onclick*="applyCartDiscount"]');
    if (!input) return;

    const code = input.value.trim().toUpperCase();
    if (!code) {
      if (msg) {
        msg.className = 'text-xs font-semibold text-[#D66152] block mt-1.5 font-franklin';
        msg.innerText = 'Bitte gib einen Rabattcode ein.';
      }
      return;
    }

    if (!LilLeoCart.cartData || LilLeoCart.cartData.item_count === 0) {
      if (msg) {
        msg.className = 'text-xs font-semibold text-[#D66152] block mt-1.5 font-franklin';
        msg.innerText = 'Bitte lege zuerst einen Artikel in den Warenkorb.';
      }
      return;
    }

    if (btn) {
      btn.disabled = true;
      btn.setAttribute('data-orig-text', btn.innerText);
      btn.innerText = 'Prüfen...';
    }

    try {
      window.__LilLeoInternalFetch = true;
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: code })
      });
      const cart = await res.json();

      const validDiscount = cart.discount_codes && cart.discount_codes.find(d => d.applicable === true);

      if (validDiscount) {
        sessionStorage.setItem('lilleo_discount_code', validDiscount.code);
        LilLeoCart.cartData = cart;
        LilLeoCart.render(cart);

        if (msg) {
          msg.className = 'text-xs font-semibold text-emerald-700 block mt-1.5 font-franklin';
          msg.innerHTML = `✓ Rabattcode "<strong>${validDiscount.code}</strong>" erfolgreich eingelöst!`;
        }
        if (typeof window.showToast === 'function') {
          window.showToast(`✓ Rabattcode "${validDiscount.code}" angewendet!`);
        }
      } else {
        // Ungültigen oder nicht anwendbaren Code aus der Shopify-Session entfernen
        const resetRes = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ discount: '' })
        });
        const resetCart = await resetRes.json();
        sessionStorage.removeItem('lilleo_discount_code');
        LilLeoCart.cartData = resetCart;
        LilLeoCart.render(resetCart);

        const saleTierActive = !!document.querySelector('[data-sale-tier-active="true"]');
        if (msg) {
          if (saleTierActive) {
            msg.className = 'text-xs font-semibold text-charcoal block mt-1.5 font-franklin';
            msg.innerText = 'Während Black November gilt automatisch der Staffelrabatt.';
          } else {
            msg.className = 'text-xs font-semibold text-[#D66152] block mt-1.5 font-franklin';
            msg.innerText = `✕ Rabattcode "${code}" ist ungültig oder nicht anwendbar.`;
          }
        }
        if (typeof window.showToast === 'function') {
          window.showToast(saleTierActive ? 'Während Black November gilt automatisch der Staffelrabatt.' : `✕ Code "${code}" ist ungültig`);
        }
      }
    } catch (err) {
      console.error('Error applying discount:', err);
      if (msg) {
        msg.className = 'text-xs font-semibold text-[#D66152] block mt-1.5 font-franklin';
        msg.innerText = 'Fehler beim Prüfen des Rabattcodes.';
      }
    } finally {
      window.__LilLeoInternalFetch = false;
      if (btn) {
        btn.disabled = false;
        btn.innerText = btn.getAttribute('data-orig-text') || 'Anwenden';
      }
    }
  };

  // Gutscheincode entfernen
  window.removeCartDiscount = async function () {
    const input = document.getElementById('cart-discount-input');
    const msg = document.getElementById('cart-discount-message');
    try {
      window.__LilLeoInternalFetch = true;
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: '' })
      });
      const cart = await res.json();
      sessionStorage.removeItem('lilleo_discount_code');
      if (input) input.value = '';
      if (msg) {
        msg.className = 'text-xs font-medium text-charcoal/70 block mt-1.5 font-franklin';
        msg.innerText = 'Rabattcode entfernt.';
        setTimeout(() => {
          if (msg && msg.innerText === 'Rabattcode entfernt.') {
            msg.className = 'text-xs sm:text-[13px] font-medium hidden';
          }
        }, 3000);
      }
      LilLeoCart.cartData = cart;
      LilLeoCart.render(cart);
      if (typeof window.showToast === 'function') {
        window.showToast('Rabattcode entfernt');
      }
    } catch (err) {
      console.error('Error removing discount:', err);
    } finally {
      window.__LilLeoInternalFetch = false;
    }
  };

  // Checkout Redirect mit Gutschein
  window.goToCheckout = function () {
    const code = sessionStorage.getItem('lilleo_discount_code');
    if (code) {
      window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
    } else {
      window.location.href = '/checkout';
    }
  };

  // Quick Add Upsell Helper
  window.quickAddUpsell = function (handleOrId, btnEl, title) {
    if (btnEl && btnEl.disabled) return;
    if (btnEl) {
      btnEl.disabled = true;
      btnEl.innerText = 'Laden...';
    }

    LilLeoCart.addItem(handleOrId, 1, title || 'Upsell Artikel');
    setTimeout(() => {
      if (btnEl) {
        btnEl.disabled = false;
        btnEl.innerText = '+ Hinzufügen';
      }
    }, 600);
  };

  // Cart Note Handler
  window.toggleCartNote = function () {
    const box = document.getElementById('cart-note-box');
    const chevron = document.getElementById('cart-note-chevron');
    if (box) box.classList.toggle('hidden');
    if (chevron) chevron.classList.toggle('rotate-180');
  };

  window.saveCartNote = async function (noteText) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: noteText })
      });
      if (res.ok) {
        const statusEl = document.getElementById('cart-note-status');
        if (statusEl) {
          statusEl.classList.remove('hidden');
          setTimeout(() => statusEl.classList.add('hidden'), 2500);
        }
      }
    } catch (e) {
      console.error('Failed to save cart note:', e);
    }
  };

  // Initialisieren sobald DOM bereit ist
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => LilLeoCart.init());
  } else {
    LilLeoCart.init();
  }
})();

